import { find, get, groupBy, maxBy, sortBy } from 'lodash';
import assert from 'assert';

const makeRecordFromAttempt = (attempt, athlete) => ({
  barbellWeight: attempt.weight,
  attemptNumber: attempt.number,
  startNumber: athlete.startNumber,
  previous: null,
  athlete,
  type: 'attempt',
  attempt,
});

const makeRecordFromDeclaration = (declaration, athlete) => ({
  barbellWeight: declaration.weight,
  attemptNumber: declaration.attemptNumber,
  startNumber: athlete.startNumber,
  previous: null,
  athlete,
  type: 'declaration',
  declaration: declaration,
});

const makeAthleteRecords = (athlete, liftId) => {
  const {
    lastDeclarationByLiftId: { [liftId]: lastDeclaration },
    attemptsByLiftId: { [liftId]: attempts = [] },
  } = athlete;

  const attemptRecords = attempts.map(attempt =>
    makeRecordFromAttempt(attempt, athlete),
  );
  for (const record of attemptRecords)
    record.previous = find(attemptRecords, {
      attemptNumber: record.attemptNumber - 1,
    });
  const lastAttemptRecord = maxBy(attemptRecords, 'attemptNumber');

  const declarationRecord =
    lastDeclaration &&
    lastDeclaration.attemptNumber > get(lastAttemptRecord, 'attemptNumber', 0)
      ? makeRecordFromDeclaration(lastDeclaration, athlete)
      : null;
  if (declarationRecord) declarationRecord.previous = lastAttemptRecord;

  return { attempts: attemptRecords, declaration: declarationRecord };
};

const sequenceRecords = (records, criteria, i = 1) => {
  const [criterion, ...remainingCriteria] = criteria;

  assert(criterion);
  if (!criterion) {
    for (const record of records) record.$sequenceNumber = i++;
    return i;
  }

  const byCriterion = groupBy(records, record => get(record, criterion, 0));
  const sortedKeys = sortBy(Object.keys(byCriterion), Number);
  for (const key of sortedKeys) {
    const criteriaRecords = byCriterion[key];
    if (criteriaRecords.length === 1) {
      criteriaRecords[0].$sequenceNumber = i++;
    } else {
      i = sequenceRecords(criteriaRecords, remainingCriteria, i);
    }
  }
  return i;
};

const computeCallingOrder = (athletes, liftId, mode) => {
  if (!athletes) return [];

  const attemptRecords = [];
  const declarationRecords = [];
  for (const athlete of athletes) {
    const athleteRecords = makeAthleteRecords(athlete, liftId);

    attemptRecords.push(...athleteRecords.attempts);
    if (athleteRecords.declaration)
      declarationRecords.push(athleteRecords.declaration);
  }

  /*
    1. The weight of the barbell (lightest weight first)
    2. The number of the attempt (lowest number first)
    3. The sequence / order of the previous attempt(s) (the athlete who lifted earliest is first)
    4. The start number of the athlete (lowest first)
    */
  const defaultCriteria = [
    'barbellWeight',
    'attemptNumber',
    'previous.$sequenceNumber',
    'startNumber',
  ];

  const roundCriteria = [
    'attemptNumber',
    'barbellWeight',
    'previous.$sequenceNumber',
    'startNumber',
  ];

  const criteria = mode === 'round' ? roundCriteria : defaultCriteria;

  let i = sequenceRecords(attemptRecords, criteria);
  sequenceRecords(declarationRecords, criteria, i);

  return sortBy(declarationRecords, '$sequenceNumber').map(
    ({ athlete }) => athlete,
  );
};

export { computeCallingOrder };



// WEBPACK FOOTER //
// ./src/rules/callingOrder.js